import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';

import {
  ME_LEGAL_CODES,
  SC_LEGAL_CODE,
  SCI_LEGAL_CODE,
} from 'register-partners-js/models/register-organization';

export default class PartnerRegistration extends Model {
  @attr() flowType;
  @attr() partnerName;
  @attr() partnerType;
  @attr() status; // pending, contract_sign_pending, contract_sign_validation_error, contract_signed
  @attr('string') prepaidSource;

  @belongsTo('register-organization', { async: true, inverse: 'partnerRegistration' })
  organization;

  @cached
  get owner() {
    return this.organization.get('owner');
  }

  @cached
  get legalFlow() {
    return this.organization.get('underRegistration') ? 'company_creation' : 'existing_company';
  }

  get isEmbedded() {
    return this.partnerType === 'embed';
  }

  get isLegalPlace() {
    return this.partnerName?.toLowerCase() === 'legalplace';
  }

  /*
   * For French Micro Enterprises and SC|I in case they don't need capital deposit (underRegistration),
   * we --skip price plan selection steps--, we "represent" this flow with this name.
   */
  get isFastTrackWithoutCapitalDeposit() {
    let isFrench = this.organization.get('legalCountry') === 'FR';
    let isCompanyCreationFromScratch = this.organization.get('companyCreationFromScratch');
    let isCompanyCreated = this.organization.get('underRegistration') === false;

    let isMicroEnterpriseOrCivilCompany = [
      ...ME_LEGAL_CODES,
      SCI_LEGAL_CODE,
      SC_LEGAL_CODE,
    ].includes(this.organization.get('legalFormCreditsafeCode'));

    return (
      isFrench &&
      isCompanyCreationFromScratch &&
      isCompanyCreated &&
      isMicroEnterpriseOrCivilCompany
    );
  }

  /**
   * For companies Legal Flow has created for us and are completing the Qonto registration. Very
   * similar to isFastTrackWithoutCapitalDeposit case, but with slightly different condition
   * @returns {boolean}
   */
  get isMicroEnterpriseCreationThoughLegalPlace() {
    let isComingFromLegalPlace = this.isLegalPlace;
    let isFrench = this.organization.get('legalCountry') === 'FR';
    let isCompanyCreationFromScratch = this.organization.get('companyCreationFromScratch');
    let isCompanyCreated = this.organization.get('underRegistration') === false;

    let isMicroEnterpriseOrCivilCompany = [
      ...ME_LEGAL_CODES,
      SCI_LEGAL_CODE,
      SC_LEGAL_CODE,
    ].includes(this.organization.get('legalFormCreditsafeCode'));

    return (
      isComingFromLegalPlace &&
      isFrench &&
      !isCompanyCreationFromScratch &&
      isCompanyCreated &&
      isMicroEnterpriseOrCivilCompany
    );
  }

  /**
   * For French Full Company Creation flow
   * @returns {boolean}
   */
  get isFrFullCompanyCreation() {
    return this.organization.get('legalCountry') === 'FR' && this.legalFlow === 'company_creation';
  }
}
